import { db } from '../db/database';
import { recipesApi, groceryApi, mealPlanApi, syncApi } from './api';

const SYNC_KEY = 'cooksmith_last_synced';

/**
 * SyncService handles bidirectional sync between IndexedDB and DynamoDB.
 * Strategy: offline-first with background push/pull.
 *
 * - All writes go to IndexedDB first (instant UI)
 * - Mutations are queued in a sync queue
 * - On connectivity, queued mutations are pushed to the API
 * - On app startup (when authenticated), a full pull merges cloud data
 */

export async function initSyncQueue() {
  // Add syncQueue table if not present (Dexie version upgrade needed)
  // For now, we store the queue in localStorage as a lightweight approach
}

function getSyncQueue() {
  try {
    return JSON.parse(localStorage.getItem('cooksmith_sync_queue') || '[]');
  } catch {
    return [];
  }
}

function addToSyncQueue(operation, resourceType, resourceId, data) {
  const queue = getSyncQueue();
  queue.push({
    id: Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
    operation, // 'create' | 'update' | 'delete'
    resourceType, // 'recipe' | 'grocery' | 'mealplan'
    resourceId,
    data,
    timestamp: new Date().toISOString(),
  });
  localStorage.setItem('cooksmith_sync_queue', JSON.stringify(queue));
}

function removeFromSyncQueue(id) {
  const queue = getSyncQueue().filter(item => item.id !== id);
  localStorage.setItem('cooksmith_sync_queue', JSON.stringify(queue));
}

// eslint-disable-next-line no-unused-vars
function clearSyncQueue() {
  localStorage.setItem('cooksmith_sync_queue', '[]');
}

// --- Queue operations for each mutation ---

export function queueRecipeCreate(recipe) {
  addToSyncQueue('create', 'recipe', recipe.id?.toString(), recipe);
}

export function queueRecipeUpdate(recipeId, updates) {
  addToSyncQueue('update', 'recipe', recipeId?.toString(), updates);
}

export function queueRecipeDelete(recipeId) {
  addToSyncQueue('delete', 'recipe', recipeId?.toString(), null);
}

export function queueMealPlanUpdate(weekStart, days) {
  addToSyncQueue('update', 'mealplan', weekStart, { days });
}

export function queueGroceryUpdate(listId, data) {
  addToSyncQueue('update', 'grocery', listId?.toString(), data);
}

// --- Push queued mutations to the cloud ---

export async function pushSyncQueue() {
  const queue = getSyncQueue();
  if (queue.length === 0) return { pushed: 0 };

  let pushed = 0;
  const errors = [];

  for (const item of queue) {
    try {
      switch (item.resourceType) {
        case 'recipe':
          if (item.operation === 'create') {
            await recipesApi.create(item.data);
          } else if (item.operation === 'update') {
            await recipesApi.update(item.resourceId, item.data);
          } else if (item.operation === 'delete') {
            await recipesApi.delete(item.resourceId);
          }
          break;

        case 'mealplan':
          if (item.operation === 'update') {
            await mealPlanApi.update(item.resourceId, item.data.days);
          }
          break;

        case 'grocery':
          if (item.operation === 'create') {
            await groceryApi.create(item.data);
          } else if (item.operation === 'update') {
            await groceryApi.update(item.resourceId, item.data);
          } else if (item.operation === 'delete') {
            await groceryApi.delete(item.resourceId);
          }
          break;

        default:
          break;
      }

      removeFromSyncQueue(item.id);
      pushed++;
    } catch (err) {
      console.warn(`Sync push failed for ${item.resourceType}/${item.resourceId}:`, err.message);
      errors.push({ item, error: err.message });
    }
  }

  return { pushed, errors };
}

// --- Pull data from the cloud and merge into IndexedDB ---

export async function pullFromCloud() {
  const lastSynced = localStorage.getItem(SYNC_KEY) || '1970-01-01T00:00:00.000Z';

  try {
    // Pull all updated items since last sync
    const { items, syncedAt } = await syncApi.pull(lastSynced);

    if (!items || items.length === 0) {
      localStorage.setItem(SYNC_KEY, syncedAt);
      return { pulled: 0 };
    }

    let pulled = 0;

    for (const item of items) {
      const sk = item._sk;

      if (sk?.startsWith('RECIPE#')) {
        const { _sk, ...recipe } = item;
        // Check if local version is newer
        const localRecipe = await db.recipes.where('id').equals(recipe.recipeId).first();
        if (!localRecipe || (recipe.updatedAt > (localRecipe.updatedAt || localRecipe.createdAt))) {
          if (localRecipe) {
            await db.recipes.update(localRecipe.id, recipe);
          } else {
            await db.recipes.add({
              ...recipe,
              id: recipe.recipeId,
            });
          }
          pulled++;
        }
      } else if (sk?.startsWith('MEALPLAN#')) {
        const { _sk, ...plan } = item;
        const existing = await db.mealPlans.where('weekStart').equals(plan.weekStart).first();
        if (existing) {
          await db.mealPlans.update(existing.id, plan);
        } else {
          await db.mealPlans.add(plan);
        }
        pulled++;
      }
      // Add more types as needed
    }

    localStorage.setItem(SYNC_KEY, syncedAt);
    return { pulled };
  } catch (err) {
    console.warn('Pull from cloud failed:', err.message);
    return { pulled: 0, error: err.message };
  }
}

// --- Full sync: push then pull ---

export async function fullSync() {
  const pushResult = await pushSyncQueue();
  const pullResult = await pullFromCloud();
  return { push: pushResult, pull: pullResult };
}

export function getLastSyncedAt() {
  return localStorage.getItem(SYNC_KEY);
}
